import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { FetchCities, FetchCompetences, FetchMethods } from "@utils/interfaces"

interface SlugMetaState {
    h1: string
}

interface PsyCatalogMetaState {
    loaded: boolean
    citiesData: FetchCities
    methodsData: FetchMethods
    competencesData: FetchCompetences
    cities: Record<string, SlugMetaState>
    methods: Record<string, SlugMetaState>
    competences: Record<string, SlugMetaState>
    cityFilterSeoH1: Record<string, string>
    cityFilterRouteKind: Record<string, "search" | "profile">
}

const initialState: PsyCatalogMetaState = {
    loaded: false,
    citiesData: [],
    methodsData: [],
    competencesData: [],
    cities: {},
    methods: {},
    competences: {},
    cityFilterSeoH1: {},
    cityFilterRouteKind: {},
}

const psyCatalogMetaSlice = createSlice({
    name: "psyCatalogMeta",
    initialState,
    reducers: {
        setPsyCatalogMetaLoaded(state, action: PayloadAction<boolean>) {
            state.loaded = action.payload
        },
        setPsyCatalogCitiesData(state, action: PayloadAction<FetchCities>) {
            state.citiesData = action.payload
        },
        setPsyCatalogMethodsData(state, action: PayloadAction<FetchMethods>) {
            state.methodsData = action.payload
        },
        setPsyCatalogCompetencesData(state, action: PayloadAction<FetchCompetences>) {
            state.competencesData = action.payload
        },
        setPsyCatalogCitiesMeta(state, action: PayloadAction<Record<string, SlugMetaState>>) {
            state.cities = action.payload
        },
        setPsyCatalogMethodsMeta(state, action: PayloadAction<Record<string, SlugMetaState>>) {
            state.methods = action.payload
        },
        setPsyCatalogCompetencesMeta(state, action: PayloadAction<Record<string, SlugMetaState>>) {
            state.competences = action.payload
        },
        setPsyCatalogCityFilterSeoH1(
            state,
            action: PayloadAction<{ key: string; h1: string | undefined }>
        ) {
            const { key, h1 } = action.payload
            if (!h1) {
                delete state.cityFilterSeoH1[key]
                return
            }
            state.cityFilterSeoH1[key] = h1
        },
        clearPsyCatalogCityFilterSeoH1(state, action: PayloadAction<string>) {
            delete state.cityFilterSeoH1[action.payload]
        },
        setPsyCatalogCityFilterRouteKind(
            state,
            action: PayloadAction<{ key: string; kind: "search" | "profile" }>
        ) {
            const { key, kind } = action.payload
            state.cityFilterRouteKind[key] = kind
        },
        clearPsyCatalogCityFilterRouteKind(state, action: PayloadAction<string>) {
            delete state.cityFilterRouteKind[action.payload]
        },
    },
})

export const psyCatalogMetaReducer = psyCatalogMetaSlice.reducer

export const {
    setPsyCatalogMetaLoaded,
    setPsyCatalogCitiesData,
    setPsyCatalogMethodsData,
    setPsyCatalogCompetencesData,
    setPsyCatalogCitiesMeta,
    setPsyCatalogMethodsMeta,
    setPsyCatalogCompetencesMeta,
    setPsyCatalogCityFilterSeoH1,
    clearPsyCatalogCityFilterSeoH1,
    setPsyCatalogCityFilterRouteKind,
    clearPsyCatalogCityFilterRouteKind,
} = psyCatalogMetaSlice.actions
